import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { TextInput, FieldLabel, PrimaryButton } from "@/components/ui-kit";

export default function Login() {
  const { login } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@goldenyears.com");
  const [password, setPassword] = useState("admin123");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F9F8F6]">
      {/* Hero side — Playground style */}
      <div
        className="hidden lg:flex relative flex-col justify-between p-12 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #0B0B10 0%, #1A1B2E 50%, #1E3A8A 100%)",
          color: "#F9F8F6",
        }}
      >
        {/* Decorative blobs */}
        <div
          aria-hidden
          className="pointer-events-none absolute -left-20 top-20 h-72 w-72 rounded-full opacity-25 blur-3xl"
          style={{ background: "#2563EB" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 bottom-16 h-80 w-80 rounded-full opacity-25 blur-3xl"
          style={{ background: "#7C3AED" }}
        />
        {/* Grain */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "4px 4px",
          }}
        />

        <div className="relative z-10 flex items-center gap-2">
          <span
            className="text-2xl tracking-tight"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
          >
            Atelier
          </span>
          <span
            className="text-2xl italic tracking-tight"
            style={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 400,
              color: "#93C5FD",
            }}
          >
            Business
          </span>
        </div>

        <div className="relative z-10 max-w-md">
          <div
            className="text-[10px] uppercase tracking-[0.28em] mb-4"
            style={{ color: "#93C5FD", fontWeight: 700 }}
          >
            {lang === "es" ? "Tu plan a 5 años" : "Your 5-year plan"}
          </div>
          <h2
            className="text-4xl xl:text-5xl leading-[1.05]"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
          >
            {lang === "es" ? (
              <>
                Donde las ideas se{" "}
                <span
                  className="italic bg-clip-text text-transparent"
                  style={{ backgroundImage: "linear-gradient(120deg, #93C5FD, #C4B5FD)" }}
                >
                  vuelven empresas
                </span>
                .
              </>
            ) : (
              <>
                Where ideas become{" "}
                <span
                  className="italic bg-clip-text text-transparent"
                  style={{ backgroundImage: "linear-gradient(120deg, #93C5FD, #C4B5FD)" }}
                >
                  companies
                </span>
                .
              </>
            )}
          </h2>

          {/* Mini mock card */}
          <div
            className="mt-8 rounded-2xl border p-4 shadow-2xl backdrop-blur-sm"
            style={{
              borderColor: "rgba(147,197,253,0.25)",
              background: "rgba(255,255,255,0.04)",
            }}
          >
            <div
              className="text-[10px] uppercase tracking-[0.2em] mb-3"
              style={{ color: "#94A3B8" }}
            >
              {lang === "es" ? "Generando plan…" : "Generating plan…"}
            </div>
            <ul className="space-y-2">
              {[
                { label: lang === "es" ? "Concepto & misión" : "Concept & mission", tone: "#F59E0B", stat: lang === "es" ? "Visión + equity" : "Vision + equity" },
                { label: lang === "es" ? "Mercado · TAM €640M" : "Market · TAM €640M", tone: "#10B981", stat: "+18%" },
                { label: lang === "es" ? "Finanzas · 5 años" : "Financials · 5 yrs", tone: "#0EA5E9", stat: "EBITDA €1,1M" },
                { label: lang === "es" ? "Pitch deck · 12 slides" : "Pitch deck · 12 slides", tone: "#EC4899", stat: lang === "es" ? "Modo inversor" : "Investor mode" },
              ].map((m, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2.5 rounded-lg border px-3 py-2"
                  style={{
                    borderColor: `${m.tone}30`,
                    background: "rgba(255,255,255,0.05)",
                  }}
                >
                  <span
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                    style={{ background: "#2563EB" }}
                  >
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 8.5l3.5 3.5L13 5" />
                    </svg>
                  </span>
                  <span className="text-[13px] text-white">{m.label}</span>
                  <span
                    className="ml-auto rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                    style={{
                      background: `${m.tone}20`,
                      color: m.tone,
                      border: `1px solid ${m.tone}40`,
                    }}
                  >
                    {m.stat}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div
          className="relative z-10 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em]"
          style={{ color: "#94A3B8" }}
        >
          <span style={{ color: "#93C5FD" }}>●</span>
          {lang === "es"
            ? "IA · 11 módulos · 5 años · 1 tarde"
            : "AI · 11 modules · 5 years · 1 afternoon"}
        </div>
      </div>

      {/* Form side */}
      <div className="flex flex-col">
        <div className="flex items-center justify-between p-6 lg:p-8">
          <Link
            to="/login"
            className="text-sm tracking-[0.2em] uppercase text-[#1A1A1A] lg:hidden"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Atelier&nbsp;Business
          </Link>
          <div className="ml-auto">
            <LanguageSwitcher />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <form
            onSubmit={onSubmit}
            className="w-full max-w-md space-y-6"
            data-testid="login-form"
          >
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#8A8886]">
                {t("auth.login")}
              </div>
              <h1
                className="mt-3 text-4xl sm:text-5xl leading-none text-[#1A1A1A]"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
              >
                {t("auth.welcome_back")}
              </h1>
              <p className="mt-3 text-sm text-[#5C5B5A]">{t("auth.tagline")}</p>
            </div>

            <div className="space-y-4">
              <div>
                <FieldLabel>{t("auth.email")}</FieldLabel>
                <TextInput
                  type="email"
                  value={email}
                  onChange={setEmail}
                  placeholder="admin@goldenyears.com"
                  testId="login-email-input"
                  required
                />
              </div>
              <div>
                <FieldLabel>{t("auth.password")}</FieldLabel>
                <TextInput
                  type="password"
                  value={password}
                  onChange={setPassword}
                  placeholder="••••••••"
                  testId="login-password-input"
                  required
                />
              </div>
            </div>

            {error && (
              <div
                className="text-sm text-[#9E2A2B] bg-[#9E2A2B]/10 border border-[#9E2A2B]/20 rounded-sm px-4 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <PrimaryButton
              type="submit"
              disabled={loading}
              testId="login-submit-btn"
              className="w-full py-3"
            >
              {loading ? t("common.loading") : t("auth.login")}
            </PrimaryButton>

            <p className="text-sm text-[#5C5B5A]">
              {t("auth.no_account")}{" "}
              <Link
                to="/register"
                className="text-[#2563EB] underline underline-offset-4 hover:text-[#1D4ED8]"
                data-testid="register-link"
              >
                {t("auth.register")}
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
